import { SecretsPublicKey } from "features/settings/secrets";

/** The value is longer than the key can encrypt; nothing was sent. */
export class SecretTooLongError extends Error {
  constructor(
    readonly bytes: number,
    readonly max: number,
  ) {
    super(`The value is ${bytes} bytes; the limit is ${max} bytes.`);
    this.name = "SecretTooLongError";
  }
}

/** WebCrypto exists only on https or localhost; elsewhere nothing can be encrypted. */
export class SecureContextError extends Error {
  constructor() {
    super("Encryption needs https or localhost");
    this.name = "SecureContextError";
  }
}

const encoder = new TextEncoder();

/** A value's size as the backend and KMS count it: UTF-8 bytes, not characters. */
export const byteLength = (value: string): number =>
  encoder.encode(value).length;

const fromBase64 = (base64: string): Uint8Array =>
  Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

const toBase64 = (bytes: ArrayBuffer): string => {
  let binary = "";
  new Uint8Array(bytes).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

/**
 * Encrypts `plaintext` with the environment's KMS public key (RSAES_OAEP_SHA_256,
 * no label) and returns the base64 ciphertext KMS can decrypt. The plaintext
 * never leaves the browser; only the result is sent.
 */
export const encryptSecret = async (
  plaintext: string,
  key: SecretsPublicKey,
): Promise<string> => {
  const bytes = encoder.encode(plaintext);
  if (bytes.length > key.max_bytes) {
    throw new SecretTooLongError(bytes.length, key.max_bytes);
  }
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new SecureContextError();
  }
  const publicKey = await subtle.importKey(
    "spki",
    fromBase64(key.public_key),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const ciphertext = await subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    bytes,
  );
  return toBase64(ciphertext);
};
